import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { runSheetGroups } from './runSheetGroups';
import '../OrderInvoice/registerInvoiceFonts';

/**
 * The run sheet, as paper.
 *
 * **One continuous list, not a page per area.** Areas are still grouped and
 * still in thana order — that is what lets dispatch split the stack — but a page
 * is filled before the next one starts, so a nineteen-area day is three or four
 * sheets rather than nineteen mostly-empty ones. An area band separates each
 * group; `minPresenceAhead` keeps a band from being orphaned at the foot of a
 * page with its first order overleaf.
 *
 * The document header and the column headings repeat on every page, so a sheet
 * pulled out of the middle of the stack still says what it is and what its
 * columns mean.
 *
 * Set in Hind Siliguri, not the invoice's Courier Prime. react-pdf does not fall
 * back across font families for missing glyphs, and this document is almost
 * entirely customer names and addresses — which are routinely Bangla. Courier
 * Prime would print those as blanks.
 */

const C = { text: '#1a1a1a', muted: '#6b6b6b', line: '#e2e2e2', headBg: '#f2f2f2', bandBg: '#e8e8e8', rule: '#1a1a1a' };

// Points, A4 Landscape, 42pt side margins — 757.89pt of content.
// The columns are budgeted to that width; address takes whatever is left, since
// it is the one field that is never short.
const COL = { tick: 22, id: 52, customer: 140, address: 280, phone: 95, price: 85, ready: 60 };

// The fixed header block is out of the flow, so the page has to reserve its
// height as padding or the first rows print underneath it.
const HEADER_HEIGHT = 74;

const s = StyleSheet.create({
  page: { fontFamily: 'HindSiliguri', color: C.text, fontSize: 9, lineHeight: 1.35, paddingTop: HEADER_HEIGHT, paddingBottom: 44, paddingHorizontal: 42 },

  header: { position: 'absolute', top: 28, left: 42, right: 42 },
  titleRow: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', borderBottomWidth: 1.2, borderBottomColor: C.rule, paddingBottom: 5 },
  title: { fontSize: 13, fontWeight: 700 },
  window: { fontSize: 8.5, color: C.muted },

  headRow: { flexDirection: 'row', backgroundColor: C.headBg, paddingVertical: 4, paddingHorizontal: 3, marginTop: 6 },
  head: { fontSize: 7.5, fontWeight: 700, letterSpacing: 0.4, color: C.muted, textTransform: 'uppercase' },

  band: { marginTop: 11 },
  bandFirst: { marginTop: 2 },
  bandInner: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', backgroundColor: C.bandBg, paddingVertical: 3.5, paddingHorizontal: 6, marginBottom: 1 },
  bandName: { fontSize: 10.5, fontWeight: 700 },
  bandCount: { fontSize: 8, color: C.muted },

  row: { flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 3, borderBottomWidth: 0.5, borderBottomColor: C.line },
  cell: { paddingRight: 6 },
  id: { fontWeight: 700 },
  muted: { color: C.muted },
  // An empty box the rider marks off as each parcel is handed over. The reason
  // this is paper and not a screen.
  tick: { width: 11, height: 11, borderWidth: 0.8, borderColor: C.muted, borderRadius: 1.5 },

  footer: { position: 'absolute', bottom: 24, left: 42, right: 42, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7.5, color: C.muted, borderTopWidth: 0.5, borderTopColor: C.line, paddingTop: 5 },
});

const money = (n) => 'Tk ' + Math.round(Number(n) || 0).toLocaleString('en-US');

/** One order's line. Never split across a page: half an address on either side
 *  of a tear is worse than a shorter page. */
const Row = ({ row }) => (
  <View style={s.row} wrap={false}>
    <View style={{ width: COL.tick }}><View style={s.tick} /></View>
    <Text style={[s.cell, s.id, { width: COL.id }]}>#{row.id}</Text>
    <Text style={[s.cell, { width: COL.customer }]}>{row.customerName}</Text>
    <Text style={[s.cell, { width: COL.address }]}>{row.address}</Text>
    <Text style={[s.cell, { width: COL.phone }]}>{row.phone}</Text>
    <Text style={[s.cell, { width: COL.price, textAlign: 'right' }]}>{money(row.totalPrice)}</Text>
    <Text style={[s.cell, s.muted, { width: COL.ready, textAlign: 'right' }]}>{timeOnly(row.becameReady)}</Text>
  </View>
);

/** 'YYYY-MM-DD HH:mm' → 'HH:mm'. The header already states the day. */
const timeOnly = (becameReady) => (becameReady || '').split(' ')[1] || '—';

const fmt = (d) => (d ? dayjs(d).format('DD MMM YYYY, HH:mm') : '');

const RunSheetDocument = ({ rows = [], areaIds = [], from, to }) => {
  const groups = runSheetGroups(rows, areaIds);
  const total = groups.reduce((n, group) => n + group.rows.length, 0);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={s.page}>
        {/* Out of the flow and repeated, so any sheet in the stack is legible
            on its own. HEADER_HEIGHT reserves the space it occupies. */}
        <View style={s.header} fixed>
          <View style={s.titleRow}>
            <Text style={s.title}>Home delivery run sheet</Text>
            <Text style={s.window}>
              {total} order{total === 1 ? '' : 's'} · ready between {fmt(from)} and {fmt(to)}
            </Text>
          </View>
          <View style={s.headRow}>
            <Text style={[s.head, { width: COL.tick }]}> </Text>
            <Text style={[s.head, { width: COL.id }]}>Order</Text>
            <Text style={[s.head, { width: COL.customer }]}>Customer</Text>
            <Text style={[s.head, { width: COL.address }]}>Address</Text>
            <Text style={[s.head, { width: COL.phone }]}>Phone</Text>
            <Text style={[s.head, { width: COL.price, textAlign: 'right' }]}>Price</Text>
            <Text style={[s.head, { width: COL.ready, textAlign: 'right' }]}>Ready</Text>
          </View>
        </View>

        {groups.map((group, index) => (
          <React.Fragment key={group.thanaId ?? group.thana}>
            {/* The band and its first order travel as one unwrappable unit, so
                an area heading can never be left at the foot of a page with its
                orders overleaf — which on a document somebody splits by area is
                an invitation to split it wrong.

                Bound to the row rather than guarded with `minPresenceAhead`:
                the reserve has to be guessed, and measured against real data a
                46pt reserve cost a whole area on every page while a 24pt one
                still orphaned a band whose next address ran to two lines. This
                keeps exactly the pair that must stay together and fills the rest
                of the page. */}
            <View style={[s.band, index === 0 && s.bandFirst]} wrap={false}>
              <View style={s.bandInner}>
                <Text style={s.bandName}>{group.thana}</Text>
                <Text style={s.bandCount}>
                  {group.rows.length} order{group.rows.length === 1 ? '' : 's'}
                </Text>
              </View>
              <Row row={group.rows[0]} />
            </View>

            {group.rows.slice(1).map((row) => <Row key={row.id} row={row} />)}
          </React.Fragment>
        ))}

        <View style={s.footer} fixed>
          <Text>Home delivery run sheet</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default RunSheetDocument;
